import { type ReactNode } from 'react'
import { QoeHelp } from '../QoeHelp'
import type { QoeHelpEntry } from '../qoeHelpContent'
import { qoeOwnerText, type QoeDensity } from './qoeOwnerLanguage'
import { QOE_STATUS_LABEL, type QoeStep, type QoeWorkspaceTab } from './qoeStages'

/**
 * One step of the guided flow.
 *
 * The teaching copy is a RENDERING of the existing verified help entry, not new prose: `what` opens
 * the step, `how[]` are the numbered actions and `limits[]` are what it does not cover. Only the
 * title and the one-line "why this matters" are written here (see `qoeStages.ts`). The "?" beside
 * the title still opens the same entry in full, so the exact rules are always one click away.
 *
 * Behind "Show the detail" the card renders the REAL workpaper panel — the same element the tab
 * renders, passed in as children. Guided collapses it, Working expands it. There is no second
 * implementation of any panel, so a control added to a panel is present at every density.
 */
export interface QoeStepCardProps {
  step: QoeStep
  /** 1-based position, shown on the card and in the rail. */
  index: number
  density: QoeDensity
  open: boolean
  onOpen: () => void
  detailOpen: boolean
  onToggleDetail: () => void
  /** Lazily loaded; until it arrives the card shows its title, why-line and findings. */
  help: QoeHelpEntry | undefined
  /** Rewrites engine finding wording only — never a number, a code, a severity or a count. */
  presentText: (value: string) => string
  onOpenWorkspace: (tab: QoeWorkspaceTab) => void
  /** The real panel for this step. Absent for a step whose evidence lives in a shared table. */
  children?: ReactNode
}

export function QoeStepCard({ step, index, density, open, onOpen, detailOpen, onToggleDetail, help, presentText, onOpenWorkspace, children }: QoeStepCardProps) {
  const { stage, status } = step
  const owner = (value: string) => qoeOwnerText(value, density)
  const panelId = `qoe-step-panel-${stage.id}`
  return <section className={`qoe-step qoe-step-${status}${open ? ' qoe-step-open' : ''}`} aria-labelledby={`qoe-step-${stage.id}`}>
    {/* The whole header is the disclosure control: an owner reading one step at a time should not
        have to find a caret. The help "?" sits outside it so opening help never closes the step. */}
    <div className="qoe-step-head">
      <button type="button" className="qoe-step-toggle" id={`qoe-step-${stage.id}`} aria-expanded={open} aria-controls={panelId} onClick={onOpen}>
        <span className="qoe-step-index" aria-hidden="true">{index}</span>
        <span className="qoe-step-title">
          <strong>{owner(stage.title)}</strong>
          <small>{owner(stage.why)}</small>
        </span>
        <span className={`qoe-badge qoe-${status}`}>{QOE_STATUS_LABEL[status]}</span>
      </button>
      <QoeHelp id={stage.helpId} />
    </div>
    {open && <div className="qoe-step-body" id={panelId}>
      {help ? <p className="qoe-step-what">{owner(help.what)}</p> : <p className="qoe-step-what">{owner(stage.why)}</p>}
      {step.findings.length > 0 && <ul className="qoe-findings qoe-step-findings">
        {step.findings.map(finding => <li key={finding.id} className={`qoe-finding-${finding.severity}`}>
          <span className={`qoe-badge qoe-${finding.severity}`}>{finding.severity === 'error' ? 'Blocking' : 'Needs you'}</span>
          <div>{presentText(finding.message)}</div>
        </li>)}
      </ul>}
      {/* A step that renders no panel substitutes its own line for the help entry's numbered
          actions: those operate a table this card does not show, and telling a reader to sort a
          column that is not on screen is worse than telling them nothing. */}
      {stage.withoutPanel
        ? <p className="qoe-step-what">{owner(stage.withoutPanel)}</p>
        : help && help.how.length > 0 && <>
          <h4>What to do</h4>
          <ol className="qoe-step-how">{help.how.map((action, i) => <li key={i}>{owner(action)}</li>)}</ol>
        </>}
      {help && help.limits.length > 0 && <details className="qoe-step-limits">
        <summary>What this step does not cover</summary>
        <ul>{help.limits.map((limit, i) => <li key={i}>{owner(limit)}</li>)}</ul>
      </details>}
      <div className="qoe-actions qoe-step-actions">
        {children && <button type="button" className="qoe-button secondary" aria-expanded={detailOpen} onClick={onToggleDetail}>
          {detailOpen ? 'Hide the detail' : 'Show the detail'}
        </button>}
        <button type="button" className="qoe-text-button" onClick={() => onOpenWorkspace(stage.tab)}>Open this in the workspace →</button>
      </div>
      {children && detailOpen && <div className="qoe-step-detail">{children}</div>}
    </div>}
  </section>
}
